import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, CircularProgress} from 'views/components';
import {Tab, TabContext, TabList, TabPanel} from 'views/components';
import {translate, Translate} from 'views/module';

import JobHeader from './header/JobHeader';
import JobInformation from './job_information/JobInformation';
import JobMessages from './messages/JobMessages';
import InternalApplications from './job_applications/InternalApplications';
import NearbyCaregivers from './nearby_caregivers/NearbyCaregivers';
import JobCandidates from './job_candidates/JobCandidates';
import JobInvitations from './job_invitations/JobInvitations';

const JobFeed = (props) => {
  const {
    feedTab,
    setFeedTab,
    applications,
    caregiverNameFilter,
    caregivers,
    caregiversLoading,
    radius,
    loomJob,
    candidates,
    candidatesLoading,
    candidateRadius,
    invitations,
  } = props;

  const handleChange = (event, newValue) => {
    setFeedTab(newValue);
  };

  const tallyByStatus = React.useCallback((list = []) => {
    return list.reduce((acc, application) => {
      switch (application?.status) {
        case 'PENDING':
          acc.P += 1;
          break;
        case 'ACCEPTED':
          acc.A += 1;
          break;
        case 'REJECTED':
          acc.R += 1;
          break;
        default:
          break;
      }
      return acc;
    }, {P: 0, A: 0, R: 0});
  }, []);

  const internalCounts = React.useMemo(() => tallyByStatus(applications || []), [applications, tallyByStatus]);

  return (
    <Stack>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <JobHeader
          {...props}
        />
      </Box>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <TabContext
          value={feedTab}
        >
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={translate('career.jobDetail.Tabs.Information')}
              value="INFORMATION"
            />
            {
              !loomJob &&
              <Tab
                label={translate('career.jobDetail.Tabs.Messages')}
                value="MESSAGES"
              />
            }
            {
              !loomJob &&
              <Tab
                label={translate('career.jobDetail.Tabs.InternalApplications', {
                  p: internalCounts.P,
                  a: internalCounts.A,
                  r: internalCounts.R,
                })}
                value="INTERNAL_APPLICATIONS"
              />
            }
            {
              !loomJob &&
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Translate
                      expression={
                        caregiverNameFilter ?
                          'career.jobDetail.Tabs.NearbyCaregiversSearch' :
                          'career.jobDetail.Tabs.NearbyCaregivers'
                      }
                      substitute={{count: caregivers.length, radius}}
                    />
                    {
                      caregiversLoading && <CircularProgress size={16} />
                    }
                  </Stack>
                }
                value="NEARBY_CAREGIVERS"
              />
            }
            {
              loomJob &&
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Translate
                      expression="career.jobDetail.Tabs.Candidates"
                      substitute={{count: candidates?.candidates.length || 0, radius: candidateRadius}}
                    />
                    {
                      candidatesLoading && <CircularProgress size={16} />
                    }
                  </Stack>
                }
                value="CANDIDATES"
              />
            }
            {
              loomJob &&
              <Tab
                label={translate('career.jobDetail.Tabs.Invitations', {count: invitations.length})}
                value="INVITATIONS"
              />
            }
          </TabList>
          <TabPanel
            value={'INFORMATION'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <JobInformation
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'MESSAGES'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <JobMessages
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'INTERNAL_APPLICATIONS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <InternalApplications
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'NEARBY_CAREGIVERS'}
            sx={{
              m: 0,
              p: 0,
            }}
          >
            <NearbyCaregivers
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CANDIDATES'}
            sx={{
              m: 0,
              p: 0,
            }}
          >
            <JobCandidates
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'INVITATIONS'}
            sx={{
              m: 0,
              p: 0,
            }}
          >
            <JobInvitations
              {...props}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </Stack>
  );
};

JobFeed.propTypes = {
  showJobTypePill: PropTypes.bool,
  feedTab: PropTypes.string.isRequired,
  setFeedTab: PropTypes.func.isRequired,
  applications: PropTypes.array,
  caregiverNameFilter: PropTypes.string.isRequired,
  caregiverStatusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  languagesFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  languagesFilterOp: PropTypes.oneOf(['AND', 'OR']).isRequired,
  caregivers: PropTypes.array.isRequired,
  caregiversLoading: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  jobRn: PropTypes.string.isRequired,
  loomJob: PropTypes.bool,
  candidates: PropTypes.object,
  candidatesLoading: PropTypes.bool,
  candidateRadius: PropTypes.number,
  invitations: PropTypes.array.isRequired,
  interviewQuestions: PropTypes.array,
  onEditJob: PropTypes.func,
  onRegenerateJob: PropTypes.func,
  setCaregiverNameFilter: PropTypes.func.isRequired,
  setCaregiverStatusFilter: PropTypes.func.isRequired,
  setLanguagesFilter: PropTypes.func.isRequired,
  setLanguagesFilterOp: PropTypes.func.isRequired,
  setRadius: PropTypes.func.isRequired,
  requester: PropTypes.object.isRequired,
};

export default React.memo(JobFeed);
